import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DG_FRESH_TEXT_MS } from '../utils/dgStatus';

import { useTTS } from '../hooks/useTTS';
import { useTranslate } from '../hooks/useTranslate';
import { useSession, safeSet } from '../contexts/SessionContext';
import { useAudioSettings } from '../contexts/AudioSettingsContext';
import { truncateDeviceLabel } from '../utils/audioSelfTest';
import {
  applyDisplayProtections,
  containsCriticalData,
  copyableDigits,
  copyableSensitiveValue,
  NUMBER_HIGHLIGHT_REGEX,
  splitHighlightSegments,
} from '../utils/sensitiveDataProtector';
import { formatTranscriptForDisplay, collectCopyableEntities, bubbleTimestampLabel } from '../utils/transcriptFormat';
import { composeCaptionTranslation } from '../utils/translationApplicator';
import { reattachLabelForMode, resolveIdleAudioMode } from '../utils/offCallIdleMessages';
// v4.169.0: per-socket STT health — the ES socket can go silent while the app
// keeps stamping every bubble with the EN socket's language, and that has to be
// visible, not inferred from two confidence percentages.
import { buildSocketHealth } from '../utils/socketHealth';
import { ScrambleText } from './ScrambleText';
import { RepeatDimText } from './RepeatDimText';
import { StableLiveTranscriptText } from './StableLiveTranscriptText';
import { buildCaptionContinuityKeys } from '../utils/stableLiveTranscript';
import { alignWordConfidence, confidenceVisualFor } from '../utils/wordConfidenceAlign';
import { flagVanish, traceCaptionArrayDiff, observeDomVanish } from '../utils/vanishTrace';
import { linkDisplayedCaption } from '../utils/sttDiagnosticTrace';
import { nextLiveHeightLock } from '../utils/liveBubbleHeight';
import {
  PROGRAMMATIC_GRACE_MS,
  RESUME_AFTER_USER_MS,
  USER_GESTURE_HOLD_MS,
  classifyScroll,
  isAtBottom,
  readScrollMetrics,
  shouldFollow,
  READING_HOLD_MS,
} from '../utils/stickyScroll';
import {
  isCaptionPinned,
  migratePinnedCaptions,
  togglePinEntry,
} from '../utils/pinnedCaptions';
import { NewcomerIdleGuide } from './NewcomerIdleGuide';
import {
  bubbleExpandKey,
  countWords as countBubbleWords,
  isCriticalBubbleText,
  shouldAutoCollapseBubble,
  ALWAYS_VISIBLE_TAIL_ROWS,
} from '../utils/bubbleCompression';
import { ConfirmDialog } from './ConfirmDialog';
import { isNewcomerGuideDismissed } from '../utils/newcomerGuide';
import { isTranslationStuckForRetranslate } from '../utils/translationQuality';
import {
  isComponentVisible,
  useComponentVisibilityRefresh,
} from '../utils/componentVisibility';
import {
  loadLanguagePair,
  isEnEsProtectionMode,
  shouldReverseBubble,
  normalizeLang,
  getOppositeLang,
  LANG_PAIR_CHANGED_EVENT,
} from '../utils/languageConfig';
import { BubbleCorrectionEditor } from './BubbleCorrectionEditor';
import {
  saveCorrection,
  applySttCorrections,
  CORRECTION_KIND,
  CORRECTIONS_CHANGED_EVENT,
} from '../utils/transcriptCorrections';
// v4.155.0 — term repair (domain lexicon). OFF by default: with the switch off
// resolveDisplayText is a pass-through and the bubble shows exactly what
// Deepgram said.
import { resolveDisplayText } from '../utils/displaySourceText';
import {
  applyUserCorrectionToCaption,
  userCorrectionPatch,
  CAPTION_PATCH_EVENT,
} from '../utils/captionEngine';

import { NEGATION_GAP_TITLE } from '../utils/negationGuard';
// v4.169.0: a translation longer than its source can be is reporting content
// that was never said. Reported, never rewritten.
import { translationSurplus, describeSurplus } from '../utils/translationSurplus';
import {
  isDomainRepairEnabled,
  DOMAIN_REPAIR_CHANGED_EVENT,
  isNegationGuardEnabled,
  NEGATION_GUARD_CHANGED_EVENT,
} from '../utils/clinicalGuards';
import { catLog } from '../utils/catLog';

/** v4.196.0: tell useDeepgram's engine about a ✎ fix (it owns the rows). */
const sendEnginePatch = (id, patch) => {
  try {
    window.dispatchEvent(new CustomEvent(CAPTION_PATCH_EVENT, { detail: { id, patch } }));
  } catch (_) {}
};

// Messages addressed TO the interpreter ("Interpreter, ..." / "Intérprete, ...") — light blue so they're never read as patient speech.
const INTERPRETER_CUE_RE = /^\s*(interpreter|int[ée]rprete|interprete)\s*[,.:;:]/i;

// EL TABLERO DE TEXTO: Aquí es donde aparece todo lo que dicen en la llamada.
// Muestra quién habla, lo traduce y te deja copiar los números con un clic.
const getBubbleStyle = (text, isCurrent, lang, pair) => {
  if (!text) return {};
  const wordCount = text.trim().split(/\s+/).length;
  const p = pair || loadLanguagePair();
  const isRight = normalizeLang(lang) === normalizeLang(p.right);

  let baseBorder = isRight ? 'rgba(16, 185, 129, 0.4)' : 'rgba(59, 130, 246, 0.4)';
  let baseBg = isRight ? 'rgba(16, 185, 129, 0.03)' : 'rgba(59, 130, 246, 0.03)';

  if (INTERPRETER_CUE_RE.test(text)) {
    // Cue wins over lang color and length warnings — it's a meta-message, not content.
    baseBorder = 'rgba(125, 211, 252, 0.85)';
    baseBg = 'rgba(125, 211, 252, 0.10)';
    return { borderLeft: `3px solid ${baseBorder}`, backgroundColor: baseBg };
  }

  if (wordCount >= 40) {
    baseBorder = 'rgba(239, 68, 68, 0.6)';
    baseBg = 'rgba(239, 68, 68, 0.03)';
  } else if (wordCount >= 34) {
    baseBorder = 'rgba(245, 158, 11, 0.6)';
    baseBg = 'rgba(245, 158, 11, 0.03)';
  }

  return { borderLeft: `3px solid ${baseBorder}`, backgroundColor: baseBg };
};

const SENSITIVE_SEGMENT_TYPES = new Set(['date', 'schedule', 'number', 'dosage', 'vitals', 'money', 'address', 'email']);

const processDisplayText = (raw, lang, applyNumberWords, protectionsActive) => {
  const spelled = formatTranscriptForDisplay(raw, lang);
  if (!protectionsActive) return spelled;
  return applyDisplayProtections(spelled, lang, { applyNumberWords });
};

/** Stable split — dates as one unit, then numbers. */
const splitDisplaySegments = (segment) => {
  if (!segment) return [];
  return splitHighlightSegments(segment);
};

const commonWordPrefixLen = (a = '', b = '') => {
  const aParts = a.match(/\S+\s*/g) || [];
  const bParts = b.match(/\S+\s*/g) || [];
  let len = 0;
  const n = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < n; i += 1) {
    if (aParts[i].trim() !== bParts[i].trim()) break;
    len += bParts[i].length;
  }
  return len;
};

/** Blue tail slice — same pipeline for bubble text + tailPreviewText. */
const resolveTailHighlight = (repairedText, rawText, tailPreviewText, lang, applyNumberWords, protectionsActive) => {
  if (!tailPreviewText?.trim() || !repairedText) return null;

  const processedTail = processDisplayText(tailPreviewText, lang, applyNumberWords, protectionsActive);
  let idx = repairedText.indexOf(processedTail);

  // Fallback when sealed bubble text diverges slightly from raw tail preview.
  if (idx < 0 && rawText?.trim().endsWith(tailPreviewText.trim())) {
    idx = Math.max(0, repairedText.length - processedTail.length);
  }
  if (idx < 0) return null;

  const highlight = repairedText.slice(idx, idx + processedTail.length);
  return {
    before: repairedText.slice(0, idx),
    highlight,
    after: repairedText.slice(idx + highlight.length),
  };
};

/** One-click copy chip for names, spelled text, etc. */
// v4.150.0: hover-only wall-clock stamp on a bubble. Local HH:MM:SS is shown;
// the tooltip carries the exact catLog (__CAT_DUMP) format so a message can be
// matched to the console logs around it (debug missing messages, hold math).
// Sits outside MemoTranslatedBubble so it never touches morph/keys/memo props.
const BubbleTs = ({ cap }) => {
  const ts = bubbleTimestampLabel(cap?.createdAt, cap?.turnId);
  if (!ts) return null;
  return (
    <span className="bubble-ts" title={`catLog: ${ts.log} UTC — matches __CAT_DUMP() lines`}>
      {ts.local}
    </span>
  );
};

const CopyChip = ({ value, label, kind = 'default' }) => {
  if (!value) return null;
  return (
    <button
      type="button"
      className={`copy-chip copy-chip--${kind}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
      }}
      title={`Click to copy ${label}: ${value}`}
    >
      <span className="copy-chip-kind">{label}</span>
      <span className="copy-chip-value">{value}</span>
      <span className="copy-chip-icon" aria-hidden>⎘</span>
    </button>
  );
};

/**
 * Bubble text renderer (source + translation lines).
 * Exported since v4.142.0 so the repeat-dim contract (textContent unchanged,
 * `.repeat-dim` on the later copy) can be pinned without booting the whole board.
 */
export const InteractiveText = ({
  text,
  scramble = true,
  applyNumberWords = false,
  lang = 'en',
  protectionsActive = true,
  tailPreviewText = null,
  wordConfidence = null,
  isFinal = true,
  prevText = '', // v4.152.0: previous bubble's text — cross-message repeats gray out
}) => {
  const copyEntities = useMemo(() => (text ? collectCopyableEntities(text, lang) : []), [text, lang]);
  const repairedText = useMemo(
    () => (text ? processDisplayText(text, lang, applyNumberWords, protectionsActive) : ''),
    [text, lang, applyNumberWords, protectionsActive],
  );
  const alignedWords = useMemo(
    // v4.121.0: lane-aware digit alignment (spoken number-words map per lane).
    () => alignWordConfidence(repairedText, wordConfidence, lang),
    [repairedText, wordConfidence, lang],
  );
  const tailSlice = useMemo(
    () => (text ? resolveTailHighlight(repairedText, text, tailPreviewText, lang, applyNumberWords, protectionsActive) : null),
    [repairedText, text, tailPreviewText, lang, applyNumberWords, protectionsActive],
  );
  const previousTextRef = useRef('');
  const liveStablePrefixLen = useMemo(() => {
    if (!scramble || !previousTextRef.current || !repairedText) return 0;
    return commonWordPrefixLen(previousTextRef.current, repairedText);
  }, [repairedText, scramble]);

  useEffect(() => {
    previousTextRef.current = repairedText;
  }, [repairedText]);

  const handleCopy = useCallback((value, type = 'number') => {
    const clean = SENSITIVE_SEGMENT_TYPES.has(type)
      ? copyableSensitiveValue(value, type)
      : copyableDigits(value);
    if (!clean) return;
    navigator.clipboard.writeText(clean);
  }, []);

  const renderPart = useCallback((seg, i, keyPrefix = '', forcePlain = false, opts = {}) => {
    const { confidenceClass = '', liveScramble = false } = opts;
    const partKey = `${keyPrefix}${i}`;
    const useScramble = scramble && !forcePlain;
    if (typeof seg === 'string') {
      if (seg && seg.match(NUMBER_HIGHLIGHT_REGEX)) {
        return (
          <span
            key={partKey}
            className={`phone-number highlight-number${confidenceClass}`}
            onClick={(e) => { e.stopPropagation(); handleCopy(seg, 'number'); }}
            title={`Click to copy: ${copyableDigits(seg)}`}
            style={{ cursor: 'copy', backgroundColor: 'rgba(252, 211, 77, 0.1)', color: '#fcd34d', padding: '0 2px', borderRadius: '2px', fontWeight: 600, display: 'inline' }}
          >
            {useScramble ? <ScrambleText value={seg} liveMode={liveScramble} /> : seg}
          </span>
        );
      }
      return useScramble ? (
        <ScrambleText key={partKey} value={seg} className={confidenceClass.trim()} liveMode={liveScramble} />
      ) : (
        <span key={partKey} className={confidenceClass.trim()}>{seg}</span>
      );
    }
    if (seg?.type && SENSITIVE_SEGMENT_TYPES.has(seg.type)) {
      const copyVal = seg.copyValue
        || (seg.type === 'number' ? copyableDigits(seg.value) : copyableSensitiveValue(seg.value, seg.type));
      const kindClass = seg.type === 'number' ? 'phone-number' : `${seg.type}-unit`;
      return (
        <span
          key={partKey}
          className={`${kindClass} highlight-number${confidenceClass}`}
          onClick={(e) => { e.stopPropagation(); handleCopy(seg.value, seg.type); }}
          title={`Click to copy: ${copyVal}`}
          style={{ cursor: 'copy', backgroundColor: 'rgba(252, 211, 77, 0.1)', color: '#fcd34d', padding: '0 2px', borderRadius: '2px', fontWeight: 600, display: 'inline' }}
        >
          {useScramble ? <ScrambleText value={seg.value} liveMode={liveScramble} /> : seg.value}
        </span>
      );
    }
    const plain = seg?.value ?? '';
    return useScramble ? (
      <ScrambleText key={partKey} value={plain} className={confidenceClass.trim()} liveMode={liveScramble} />
    ) : (
      <span key={partKey} className={confidenceClass.trim()}>{plain}</span>
    );
  }, [scramble, handleCopy]);

  const renderSegment = useCallback((segment, keyPrefix = '', forcePlain = false, opts = {}) => {
    if (!segment) return null;
    return splitDisplaySegments(segment).map((p, i) => renderPart(p, i, keyPrefix, forcePlain, opts));
  }, [renderPart]);

  const renderConfidenceText = useCallback((
    value = repairedText,
    wordOffset = 0,
    forcePlain = !scramble,
    keyRoot = 'cw',
    liveTail = false,
  ) => {
    if (!value) return null;

    const segments = splitDisplaySegments(value);
    let runningWordOffset = wordOffset;
    const opts = { liveScramble: !forcePlain && liveTail };
    const nodes = [];

    segments.forEach((seg, segIdx) => {
      if (seg?.type && SENSITIVE_SEGMENT_TYPES.has(seg.type)) {
        nodes.push(renderPart(seg, segIdx, `${keyRoot}-seg-`, forcePlain, opts));
        runningWordOffset += (seg.value || '').trim().split(/\s+/).filter(Boolean).length;
        return;
      }

      const chunk = seg?.value ?? (typeof seg === 'string' ? seg : '');
      if (!chunk) return;

      if (!alignedWords?.length) {
        nodes.push(...splitDisplaySegments(chunk).map((p, i) => renderPart(p, i, `${keyRoot}-seg-${segIdx}-`, forcePlain, opts)));
        runningWordOffset += chunk.trim().split(/\s+/).filter(Boolean).length;
        return;
      }

      let localWi = 0;
      chunk.split(/(\s+)/).forEach((token, idx) => {
        if (!token) return;
        if (/^\s+$/.test(token)) {
          nodes.push(<span key={`${keyRoot}-seg-${segIdx}-sp-${idx}`}>{token}</span>);
          return;
        }
        const meta = alignedWords[runningWordOffset + localWi] || null;
        localWi += 1;
        const visual = confidenceVisualFor(meta?.confidence, isFinal);
        const title = Number.isFinite(meta?.confidence)
          ? `Deepgram confidence ${Math.round(meta.confidence * 100)}%`
          : undefined;
        nodes.push(
          <span
            key={`${keyRoot}-seg-${segIdx}-w-${idx}`}
            className={`confidence-word${visual.className ? ` ${visual.className}` : ''}`}
            style={{ color: visual.color, opacity: visual.opacity }}
            title={title}
          >
            {token}
          </span>,
        );
      });
      runningWordOffset += localWi;
    });

    return nodes;
  }, [alignedWords, isFinal, repairedText, renderPart, scramble]);

  const renderLiveDiffText = useCallback(() => {
    if (!scramble || liveStablePrefixLen <= 0 || liveStablePrefixLen >= repairedText.length) return null;
    const stable = repairedText.slice(0, liveStablePrefixLen);
    const tail = repairedText.slice(liveStablePrefixLen);
    const stableWords = stable.trim() ? stable.trim().split(/\s+/).length : 0;
    return (
      <>
        {alignedWords?.length
          ? renderConfidenceText(stable, 0, true, 'stable', false)
          : renderSegment(stable, 'stable-', true)}
        <span className="transcript-live-tail">
          {alignedWords?.length
            ? renderConfidenceText(tail, stableWords, false, 'tail', true)
            : renderSegment(tail, 'tail-', false, { liveScramble: true })}
        </span>
      </>
    );
  }, [alignedWords, liveStablePrefixLen, repairedText, renderConfidenceText, renderSegment, scramble]);

  if (!text) return null;

  // Phase A: chips only on sealed bubbles; trailing (after text), not a slab above.
  // Phase D: spelling stays spoken paragraph; Spelled chip via collectCopyableEntities.
  const chipRow = isFinal && copyEntities.length > 0 ? (
    <span className="copy-chip-row copy-chip-row--trailing">
      {copyEntities.map((ent) => (
        <CopyChip key={`${ent.kind}-${ent.value}`} value={ent.value} label={ent.label} kind={ent.kind} />
      ))}
    </span>
  ) : null;

  if (tailSlice) {
    const beforeWords = tailSlice.before.trim() ? tailSlice.before.trim().split(/\s+/).length : 0;
    const highlightWords = tailSlice.highlight.trim() ? tailSlice.highlight.trim().split(/\s+/).length : 0;
    return (
      <>
        {alignedWords.length
          ? renderConfidenceText(tailSlice.before, 0, true, 'pre', false)
          : renderSegment(tailSlice.before, 'pre-')}
        <span className="transcript-tail-preview">
          {alignedWords.length
            ? renderConfidenceText(tailSlice.highlight, beforeWords, true, 'tail', false)
            : renderSegment(tailSlice.highlight, 'tail-', true)}
        </span>
        {alignedWords.length
          ? renderConfidenceText(tailSlice.after, beforeWords + highlightWords, true, 'post', false)
          : renderSegment(tailSlice.after, 'post-')}
        {chipRow}
      </>
    );
  }

  const liveDiffText = renderLiveDiffText();
  if (liveDiffText) {
    return (
      <>
        {liveDiffText}
        {chipRow}
      </>
    );
  }

  // v4.142.0 — readability net (display-only): a phrase that already appeared in
  // this same text is DIMMED (`.repeat-dim`, ≥70% visible), never removed.
  // Chunks split at word boundaries, so each chunk keeps the confidence-word
  // offsets it would have had in the whole line. Scrambling paths are untouched.
  if (!scramble) {
    return (
      <>
        <RepeatDimText
          text={repairedText}
          prevText={prevText}
          renderChunk={(chunk, part) =>
            renderConfidenceText(chunk, part.wordOffset, true, `rd-${part.index}`, false)}
        />
        {chipRow}
      </>
    );
  }

  const confidenceText = renderConfidenceText(repairedText, 0, !scramble, 'cw', scramble && !isFinal);
  if (confidenceText) {
    return (
      <>
        {confidenceText}
        {chipRow}
      </>
    );
  }

  return (
    <>
      {renderSegment(repairedText)}
      {chipRow}
    </>
  );
};

const MemoInteractiveText = React.memo(InteractiveText);
const BubbleRail = ({
  engineStatus,
  turnWordCount,
  showTurnWordCount,
  isThisPlaying,
  canPlay,
  onPlayClick,
  translationFallback = false,
  negationGaps = [], // v4.156.0: read-only warning, costs zero vertical space
  // v4.169.0: surplus info for THIS bubble. Read-only, like the negation flag —
  // it tells the operator the translation may not be this line's, and never
  // guesses which part is wrong.
  translationSurplusInfo = null,
}) => {
  const steps = ['translating', 'processing', 'ready'];
  const currentIndex = steps.indexOf(engineStatus === 'buffering' ? 'processing' : engineStatus);
  const wc = turnWordCount || 0;
  const showWc = showTurnWordCount && wc > 0;
  const wcColor = wc >= 40 ? 'var(--danger)' : wc >= 34 ? '#f59e0b' : 'var(--text-muted)';
  const tprTitle = 'T=transcribe · P=process · R=ready · Word count = whole turn (resets after ~2.5s silence)';

  return (
    <div className="bubble-rail" title={tprTitle}>
      <svg className="bubble-rail-tpr" width="11" height="8" viewBox="0 0 11 8" aria-hidden>
        {[0, 1, 2].map((i) => (
          <rect
            key={i}
            x={i * 4}
            y="0"
            width="2.5"
            height="8"
            rx="0.5"
            fill={
              i <= currentIndex
                ? i === 2
                  ? translationFallback ? '#f59e0b' : '#10b981'
                  : '#3b82f6'
                : 'rgba(255,255,255,0.12)'
            }
          />
        ))}
      </svg>
      {/* v4.156.0: the ⚠ shares the word-count line, so the marker costs the
          layout ZERO extra height — transcription keeps the viewport. */}
      {(negationGaps.length > 0 || translationSurplusInfo?.suspect || showWc) && (
        <span className="bubble-rail-meta">
          {/* v4.169.0: the translation may not belong to this line. Same
              zero-height trick as the negation flag — it shares the word-count
              line, so transcription keeps the viewport. This REPORTS; it never
              rewrites. On a real call an 8-word English bubble carried 24 words
              of Spanish containing two clauses nobody said.
              `.suspect`, not truthiness: translationSurplus() always returns an
              object, so testing the object rendered an empty ⚠ on every clean
              translation. */}
          {translationSurplusInfo?.suspect && (
            <span
              className="bubble-rail-surplus"
              title={describeSurplus(translationSurplusInfo)}
              aria-label={describeSurplus(translationSurplusInfo)}
              role="img"
            >
              ⚠
            </span>
          )}
          {negationGaps.length > 0 && (
            <span
              className="bubble-rail-negation"
              title={`${NEGATION_GAP_TITLE} Expected: ${negationGaps.map((g) => g.expect).join(' / ')}`}
              aria-label={NEGATION_GAP_TITLE}
              role="img"
            >
              ⚠
            </span>
          )}
          {showWc && (
            <span className="bubble-rail-wc" style={{ color: wcColor }} title={`${wc} words`}>
              {wc > 99 ? '99' : wc}
            </span>
          )}
        </span>
      )}
      <button
        type="button"
        className="bubble-rail-play"
        onClick={onPlayClick}
        disabled={!isThisPlaying && !canPlay}
        title={isThisPlaying ? 'Stop' : canPlay ? 'Play translation' : 'Waiting for audio'}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden>
          {isThisPlaying ? (
            <>
              <rect x="2" y="2" width="3" height="8" fill="currentColor" />
              <rect x="7" y="2" width="3" height="8" fill="currentColor" />
            </>
          ) : (
            <path d="M2 1.5 L9 6 L2 10.5 Z" fill="currentColor" />
          )}
        </svg>
      </button>
    </div>
  );
};

/**
 * Exported for tests (v4.169.0). The two read-only warnings added in this
 * release — the per-socket health line and the translation-surplus flag — are
 * only worth anything if they actually render, and a component that cannot be
 * rendered in a test is a component whose warnings silently stop working.
 */
export const TranslatedBubble = ({
  id,
  text,
  lang,
  playTTS,
  stopTTS,
  playingUrl,
  prefetchTTS,
  reverse = false,
  ttsMode,
  turnWordCount,
  showTurnWordCount,
  shouldPrefetch,
  isPinned,
  onTogglePin,
  forceTranslateKey = 0,
  onManualRetranslate,
  tailPreviewText = null,
  wordConfidence = null,
  isFinal = true,
  allowAutoRetranslate = true,
  languagePair = null,
  protectionsActive = true,
  mockTranslation = null,
  userCorrected = false,
  userTranslationOverride = null,
  persistedTranslations = null,
  onPersistTranslation = null,
  onEditSource,
  onEditTranslation,
  canEdit = true,
  correctionsRev = 0,
  domainRepairOn = false, // v4.155.0: term repair switch (OFF unless the operator turns it on)
  negationGuardOn = false, // v4.156.0: read-only negation warning (own switch)
  continuityKey = '',
  prevText = '', // v4.152.0: previous bubble's text (cross-message repeat dimming)
  prevTranslationText = '', // previous bubble's translation, same purpose
}) => {
  const { translationMood } = useSession();
  const { text: displaySourceText, negated: negationGaps } = useMemo(() => {
    const r = resolveDisplayText({ text, lang, userCorrected, domainRepairOn });
    // v4.156.0: every lexicon change is logged, so after a call you can ask
    // "did it touch anything?" and get an answer (__CAT_DUMP('[domain-repair]')).
    if (r.repairs.length) catLog('[domain-repair]', { id, lang, repairs: r.repairs });
    if (r.negated.length) {
      // READ-ONLY: a dropped "denies" changes what the patient agreed to, so it
      // must reach a human — we never invent the word ourselves.
      catLog('[domain-repair] negation gap?', { id, lang, gaps: r.negated });
    }
    return { text: r.text, negated: r.negated };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, lang, userCorrected, correctionsRev, domainRepairOn, id]);

  const { translation, audioUrl, engineStatus, translationMeta, targetLang, isStale } = useTranslate(
    displaySourceText,
    lang,
    prefetchTTS,
    shouldPrefetch,
    translationMood,
    forceTranslateKey,
    {
      isFinal,
      mockTranslation,
      userTranslationOverride,
      captionId: id,
      persistedTranslations,
      onPersistTranslation,
    },
  );
  const isThisPlaying = playingUrl && audioUrl && playingUrl === audioUrl;
  const transcriptColor = INTERPRETER_CUE_RE.test(displaySourceText || text)
    ? '#bae6fd' // light blue text for "Interpreter, ..." messages (matches bubble tint)
    : '#ffffff';
  const translationColor = '#a1a1aa';

  const isTranslationStuck = isTranslationStuckForRetranslate(
    displaySourceText,
    translation,
    lang,
    targetLang,
    translationMeta,
  );
  const isTranslationMissing = engineStatus === 'ready' && !translation?.trim();

  // v4.169.0: a translation that is far longer than its source is reporting
  // content the speaker never said. On a real call an 8-word English bubble
  // carried 24 words of Spanish with two clauses nobody uttered — and the
  // operator had no way to know. REPORT ONLY: this decides nothing, rewrites
  // nothing, and is deliberately allowed to miss far more than it fires.
  const translationSurplusInfo = useMemo(
    () => (translation ? translationSurplus(displaySourceText || text, translation, lang) : null),
    [translation, displaySourceText, text, lang],
  );

  // UX: if a message is clearly "untranslated", we should just try again,
  // and only show a ↻ button as a fallback.
  const shouldAutoRetranslate =
    allowAutoRetranslate &&
    !isPinned &&
    engineStatus === 'ready' &&
    (isTranslationStuck || isTranslationMissing);
  const [autoRetranslatePending, setAutoRetranslatePending] = useState(false);
  const [hasAutoRetranslated, setHasAutoRetranslated] = useState(false);

  useEffect(() => {
    setAutoRetranslatePending(false);
    setHasAutoRetranslated(false);
  }, [id]);

  useEffect(() => {
    if (!shouldAutoRetranslate) return;
    if (hasAutoRetranslated || autoRetranslatePending) return;

    setAutoRetranslatePending(true);
    const t = setTimeout(() => {
      onManualRetranslate?.();
      setHasAutoRetranslated(true);
      setAutoRetranslatePending(false);
    }, 200);

    return () => clearTimeout(t);
  }, [shouldAutoRetranslate, hasAutoRetranslated, autoRetranslatePending, onManualRetranslate]);

  const isProblemTranslation = isTranslationStuck || isTranslationMissing;
  const showManualRetranslateBtn =
    !isPinned && engineStatus === 'ready' && isProblemTranslation && hasAutoRetranslated && !autoRetranslatePending;
  const showEdit = canEdit && isFinal && onEditSource;
  const openSourceEdit = () => onEditSource?.(displaySourceText);
  const openTranslationEdit = () => onEditTranslation?.(translation || userTranslationOverride || '');
  const sourceUsesNumberWords =
    protectionsActive && normalizeLang(lang) === 'en';
  const targetUsesNumberWords =
    protectionsActive && normalizeLang(targetLang) === 'en';
  // v4.84: never scramble live STT source; sealed source stays plain (no typewriter).
  const sourceScramble = false;
  const targetScramble = false;
  const translationFailed =
    engineStatus === 'ready' && !translation?.trim() && translationMeta?.quality === 'failed';
  const translationIsSourceFallback = Boolean(translationMeta?.passthrough);
  const translationTitle = translationFailed
    ? 'Translation failed — check Settings → Translation for engine status'
    : undefined;
  const useStableLiveSource = !isFinal;

  return (
    <div className={`translated-bubble-row${reverse ? ' is-reverse' : ''}${userCorrected || userTranslationOverride ? ' is-user-corrected' : ''}${useStableLiveSource ? ' is-live-source' : ' is-sealed-source'}`}>
      <div
        className={`bubble-col bubble-col-source${showEdit ? ' bubble-col--editable' : ''}`}
        style={{ textAlign: reverse ? 'right' : 'left', position: 'relative' }}
        onDoubleClick={showEdit ? openSourceEdit : undefined}
      >
        <div className="bubble-line" style={{ color: transcriptColor }}>
          {useStableLiveSource ? (
            <StableLiveTranscriptText
              text={displaySourceText}
              lang={lang}
              applyNumberWords={sourceUsesNumberWords}
              protectionsActive={protectionsActive}
              continuityKey={continuityKey || id}
              wordConfidence={wordConfidence}
            />
          ) : (
            <MemoInteractiveText
              text={displaySourceText}
              scramble={sourceScramble}
              applyNumberWords={sourceUsesNumberWords}
              lang={lang}
              protectionsActive={protectionsActive}
              tailPreviewText={null}
              wordConfidence={wordConfidence}
              isFinal={isFinal}
              prevText={prevText}
            />
          )}
        </div>
        {showEdit && (
          <button
            type="button"
            className="bubble-edit-btn"
            onClick={openSourceEdit}
            title="Fix transcription (double-click)"
            aria-label="Fix transcription"
          >
            ✎
          </button>
        )}
      </div>

      <div
        data-guide="bubble-rail"
        className="bubble-col bubble-col-rail"
        style={{ position: 'relative', minWidth: '6ch' }}
      >
        {showManualRetranslateBtn ? (
          <button
            type="button"
            className="bubble-rail-retranslate-btn"
            onClick={onManualRetranslate}
            title="Retrigger translation for this message"
            aria-label="Retrigger translation"
          >
            {reverse ? '←' : '→'} ↻
          </button>
        ) : (
          <BubbleRail
            engineStatus={engineStatus}
            turnWordCount={turnWordCount}
            showTurnWordCount={showTurnWordCount}
            isThisPlaying={isThisPlaying}
            canPlay={Boolean(translation && audioUrl)}
            onPlayClick={() => (isThisPlaying ? stopTTS() : playTTS(translation, targetLang, audioUrl))}
            translationFallback={translationIsSourceFallback}
            negationGaps={negationGuardOn ? negationGaps : []}
            translationSurplusInfo={translationSurplusInfo}
          />
        )}
      </div>

      <div
        className={`bubble-col bubble-col-translation${showEdit ? ' bubble-col--editable' : ''}`}
        style={{ color: translationColor, textAlign: 'left', position: 'relative' }}
        title={translationTitle}
        onDoubleClick={showEdit ? openTranslationEdit : undefined}
      >
        <div className="bubble-line bubble-line-translation">
          {translation ? (
            translationIsSourceFallback ? (
              null
            ) : (
              <>
                <MemoInteractiveText text={translation} scramble={targetScramble} applyNumberWords={targetUsesNumberWords} lang={targetLang} protectionsActive={protectionsActive} prevText={prevTranslationText} />
                {(isStale || engineStatus === 'translating') && (
                  <span className="bubble-stale-badge" title="Source updated — refreshing translation…" style={{ opacity: 0.45, fontSize: '0.65rem', marginLeft: '0.35rem' }}>↻</span>
                )}
              </>
            )
          ) : translationFailed ? (
            <span style={{ opacity: 0.3, fontSize: '0.7rem' }}>⚠️ translation failed</span>
          ) : autoRetranslatePending || engineStatus === 'translating' ? (
            <span style={{ opacity: 0.2 }}>...</span>
          ) : engineStatus === 'ready' ? (
            <span style={{ opacity: 0.25, fontSize: '0.7rem' }}>…</span>
          ) : (
            <span style={{ opacity: 0.2 }}>...</span>
          )}
        </div>
        {showEdit && (
          <button
            type="button"
            className="bubble-edit-btn bubble-edit-btn--translation"
            onClick={openTranslationEdit}
            title="Fix translation (double-click)"
            aria-label="Fix translation"
          >
            ✎
          </button>
        )}
      </div>
    </div>
  );
};

const translatedBubblePropsEqual = (prev, next) =>
  prev.id === next.id &&
  prev.text === next.text &&
  prev.lang === next.lang &&
  prev.isFinal === next.isFinal &&
  prev.tailPreviewText === next.tailPreviewText &&
  prev.wordConfidence === next.wordConfidence &&
  prev.forceTranslateKey === next.forceTranslateKey &&
  prev.allowAutoRetranslate === next.allowAutoRetranslate &&
  prev.reverse === next.reverse &&
  prev.showTurnWordCount === next.showTurnWordCount &&
  prev.turnWordCount === next.turnWordCount &&
  prev.shouldPrefetch === next.shouldPrefetch &&
  prev.isPinned === next.isPinned &&
  prev.ttsMode === next.ttsMode &&
  prev.playingUrl === next.playingUrl &&
  prev.protectionsActive === next.protectionsActive &&
  prev.userCorrected === next.userCorrected &&
  prev.userTranslationOverride === next.userTranslationOverride &&
  prev.persistedTranslations === next.persistedTranslations &&
  prev.onPersistTranslation === next.onPersistTranslation &&
  prev.canEdit === next.canEdit &&
  prev.prevText === next.prevText &&
  prev.prevTranslationText === next.prevTranslationText &&
  prev.correctionsRev === next.correctionsRev &&
  prev.domainRepairOn === next.domainRepairOn && // v4.155.0
  prev.negationGuardOn === next.negationGuardOn && // v4.156.0
  prev.continuityKey === next.continuityKey &&
  prev.languagePair?.left === next.languagePair?.left &&
  prev.languagePair?.right === next.languagePair?.right;

const MemoTranslatedBubble = React.memo(TranslatedBubble, translatedBubblePropsEqual);

export const TranscriptionBoard = ({
  captions,
  onClearAll,
  onReconnect,
  lastDataTime,
  connectProgress = null,
  connectionState = 'disconnected',
  audioAttached = false,
  micTestMode = false,
  audioSourceMode = 'tab',
}) => {
  const bottomRef = useRef(null);
  const scrollAreaRef = useRef(null);
  // v4.145.0 sticky-bottom state — see src/utils/stickyScroll.js
  const userScrolledUpRef = useRef(false);   // operator read back — do not yank
  const userGestureRef = useRef(false);      // wheel/drag/scrollbar in progress
  const gestureTimerRef = useRef(null);
  const programmaticUntilRef = useRef(0);    // scrolls we caused ourselves
  const resumeTimerRef = useRef(null);
  const lastCountRef = useRef(0);
  const [newBelow, setNewBelow] = useState(0); // lines waiting while paused
  const STICKY_BOTTOM_KEY = 'catint_sticky_bottom_v1';
  const [stickyBottom, setStickyBottom] = useState(() => {
    try {
      return localStorage.getItem(STICKY_BOTTOM_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const stickyBottomRef = useRef(stickyBottom);
  stickyBottomRef.current = stickyBottom;
  const ttsMode = 'manual';
  const [pinnedCaptions, setPinnedCaptions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('catint_pinned_msgs')) || [];
    } catch {
      return [];
    }
  });
  const [translationBumps, setTranslationBumps] = useState({});
  const [newcomerSessionHidden, setNewcomerSessionHidden] = useState(false);
  const [languagePair, setLanguagePair] = useState(loadLanguagePair);
  const protectionsActive = isEnEsProtectionMode(languagePair);
  const { playTTS, stopTTS, isPlaying, playingUrl, prefetchTTS } = useTTS();
  const { isActive, isZombieCall, lastCallSummary, setLastCallSummary, updateCaptions, lastCallArchive, clearLastCallArchive } = useSession();
  const [lastCallOpen, setLastCallOpen] = useState(true);

  /** Persist sealed translation entries onto caption.translations (IDB via updateCaptions). */
  const persistCaptionTranslation = useCallback((capId, entry) => {
    if (!capId || !entry?.key) return;
    updateCaptions((prev) =>
      prev.map((c) => {
        if (c.id !== capId) return c;
        if (c.isFinal === false) return c; // sealed/final only
        const translations = { ...(c.translations || {}), [entry.key]: entry };
        return { ...c, translations };
      }),
    );
  }, [updateCaptions]);

  useComponentVisibilityRefresh();
  const showOffCallGuide = isComponentVisible('off_call_guide', { isActive, isZombieCall });
  const { inputDevices, outputDevices, selectedMicId, selectedSinkId } = useAudioSettings();
  const ioRouteHint = React.useMemo(() => {
    const mic = inputDevices.find((d) => d.deviceId === selectedMicId);
    const out = outputDevices.find((d) => d.deviceId === selectedSinkId);
    const micLabel = truncateDeviceLabel(mic?.label || 'Default mic');
    const outLabel = truncateDeviceLabel(out?.label || 'Default out');
    return `🎤 ${micLabel} → 🔊 ${outLabel}`;
  }, [inputDevices, outputDevices, selectedMicId, selectedSinkId]);
  const [popover, setPopover] = useState({ show: false, x: 0, y: 0, text: '' });
  const popoverTimerRef = useRef(null);
  const [correctionsRev, setCorrectionsRev] = useState(0);
  // v4.155.0 — term repair switch, read live so flipping it in Settings
  // re-renders the existing bubbles immediately (no reconnect needed).
  const [domainRepairOn, setDomainRepairOn] = useState(isDomainRepairEnabled);
  useEffect(() => {
    const onChange = (e) =>
      setDomainRepairOn(typeof e?.detail === 'boolean' ? e.detail : isDomainRepairEnabled());
    window.addEventListener(DOMAIN_REPAIR_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(DOMAIN_REPAIR_CHANGED_EVENT, onChange);
  }, []);

  // v4.156.0 — negation guard switch. Read-only, and deliberately independent
  // of the repair switch: the safe half must not hide behind the risky half.
  const [negationGuardOn, setNegationGuardOn] = useState(isNegationGuardEnabled);
  useEffect(() => {
    const onChange = (e) =>
      setNegationGuardOn(typeof e?.detail === 'boolean' ? e.detail : isNegationGuardEnabled());
    window.addEventListener(NEGATION_GUARD_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(NEGATION_GUARD_CHANGED_EVENT, onChange);
  }, []);
  const [correctionEditor, setCorrectionEditor] = useState(null);
  const [footerStatus, setFooterStatus] = useState('');
  const [clearLogConfirmOpen, setClearLogConfirmOpen] = useState(false);
  const [sttNow, setSttNow] = useState(Date.now());
  const liveBubbleHeightsRef = useRef(new Map());
  const lastRenderTraceRef = useRef('');
  const prevCaptionsRef = useRef([]);
  // v4.93.2 CPU fix: log each blank caption id once per session, not per render.
  const blankFlaggedRef = useRef(new Set());
  const [, setLiveHeightRev] = useState(0);

  useEffect(() => {
    const onCorrections = () => setCorrectionsRev((n) => n + 1);
    window.addEventListener(CORRECTIONS_CHANGED_EVENT, onCorrections);
    return () => window.removeEventListener(CORRECTIONS_CHANGED_EVENT, onCorrections);
  }, []);

  useEffect(() => {
    if (connectionState !== 'connected' || !connectProgress?.audioChunksSent) return undefined;
    // v4.93.1 CPU fix: was 250ms (4 full-board renders/sec all call long).
    // Consumers only need ~1s freshness (audio-hot window 1400ms, socket
    // windows 4500ms, step "Xms ago" titles).
    const id = window.setInterval(() => setSttNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [connectProgress?.audioChunksSent, connectionState]);

  useEffect(() => {
    traceCaptionArrayDiff(prevCaptionsRef.current, captions, 'TranscriptionBoard.captions');
    prevCaptionsRef.current = captions;
    for (const caption of captions) {
      if (!caption.sttEventId || !caption.id) continue;
      linkDisplayedCaption(
        caption.sttEventId,
        caption.id,
        applySttCorrections(caption.text || '', caption.lang),
      );
    }
  }, [captions]);

  // DOM-level vanish net: catches bubble remove/relocate that state diffing misses.
  useEffect(() => observeDomVanish(scrollAreaRef.current), []);

  useEffect(() => {
    const lastCap = captions[captions.length - 1];
    if (!lastCap) return;
    const key = `${captions.length}|${lastCap.id}|${lastCap.text}|${lastCap.isFinal}`;
    if (lastRenderTraceRef.current === key) return;
    lastRenderTraceRef.current = key;
    if (typeof window !== 'undefined') {
      const entry = {
        at: new Date().toISOString(),
        ms: Math.round(performance.now()),
        stage: '7 string rendered in TranscriptionBoard',
        rows: captions.length,
        id: lastCap.id,
        chars: lastCap.text?.length || 0,
        text: (lastCap.text || '').slice(0, 160),
        wordConfidenceCount: lastCap.wordConfidence?.length || 0,
        isFinal: lastCap.isFinal !== false,
      };
      const trace = (window.__catintSttTrace ??= []);
      trace.push(entry);
      if (trace.length > 300) trace.splice(0, trace.length - 300);
      // v4.194.0: per-interim lines → ring only (was console spam on every word).
      catLog('[CAT STT] 7 string rendered in TranscriptionBoard', entry);
      if ((lastCap.text || '').trim() && !(lastCap.wordConfidence?.length)) {
        catLog('[CAT STT] rendered without word confidence', {
          id: lastCap.id,
          chars: lastCap.text?.length || 0,
          text: (lastCap.text || '').slice(0, 160),
        });
      }
    }
  }, [captions]);

  const openCorrectionEditor = useCallback((cap, field, draft, extra = {}) => {
    const sourceLang = normalizeLang(cap.lang);
    const targetLang = getOppositeLang(sourceLang, languagePair);
    setCorrectionEditor({
      capId: cap.id,
      field,
      draft: draft || '',
      sttHeard: extra.sttHeard ?? cap.sttHeard ?? cap.text ?? '',
      sourceLang,
      targetLang,
      sourceText: cap.text || '',
    });
  }, [languagePair]);

  const closeCorrectionEditor = useCallback(() => setCorrectionEditor(null), []);

  const handleCorrectionSave = useCallback(() => {
    if (!correctionEditor) return;
    const { capId, field, draft, sttHeard, sourceLang, targetLang, sourceText } = correctionEditor;
    const trimmed = (draft || '').trim();
    if (!trimmed) return;

    if (field === 'source') {
      saveCorrection({
        sourceHeard: sttHeard,
        corrected: trimmed,
        lang: sourceLang,
        kind: CORRECTION_KIND.STT,
      });
      // v4.196.0: lane buffers + engine too, or the next Deepgram packet reverts the fix.
      updateCaptions((prev) =>
        prev.map((c) => {
          if (c.id !== capId) return c;
          sendEnginePatch(capId, userCorrectionPatch(c, trimmed, sttHeard, languagePair));
          return applyUserCorrectionToCaption(c, trimmed, sttHeard, languagePair);
        }),
      );
      setTranslationBumps((prev) => ({ ...prev, [capId]: (prev[capId] ?? 0) + 1 }));
    } else {
      saveCorrection({
        sourceHeard: sourceText,
        corrected: trimmed,
        lang: sourceLang,
        targetLang,
        kind: CORRECTION_KIND.GLOSSARY,
      });
      sendEnginePatch(capId, { userTranslationOverride: trimmed });
      updateCaptions((prev) =>
        prev.map((c) => (c.id === capId ? { ...c, userTranslationOverride: trimmed } : c)),
      );
    }
    setCorrectionEditor(null);
  }, [correctionEditor, updateCaptions, languagePair]);

  const makeEditHandlers = useCallback(
    (cap) => ({
      onEditSource: (displayText) =>
        openCorrectionEditor(cap, 'source', displayText, { sttHeard: cap.sttHeard || cap.text }),
      onEditTranslation: (currentTranslation) =>
        openCorrectionEditor(cap, 'translation', currentTranslation || cap.userTranslationOverride || ''),
    }),
    [openCorrectionEditor],
  );

  const rememberLiveBubbleHeight = useCallback((id, node, textLen = 0) => {
    if (!id || !node) return;
    const height = Math.ceil(node.getBoundingClientRect().height);
    const action = nextLiveHeightLock(liveBubbleHeightsRef.current.get(id), height, textLen);
    if (!action) return;
    if (action.release) {
      // v4.84.10: shrunk text must drop the lock entirely (old code re-locked at
      // the inflated measured height — the "void" after seal-splits).
      liveBubbleHeightsRef.current.delete(id);
      setLiveHeightRev((n) => n + 1);
      return;
    }
    liveBubbleHeightsRef.current.set(id, action.set);
    if (action.rerender) setLiveHeightRev((n) => n + 1);
  }, []);

  useEffect(() => {
    const activeLiveIds = new Set(
      captions.filter((cap) => cap?.id && cap.isFinal === false).map((cap) => cap.id),
    );
    liveBubbleHeightsRef.current.forEach((_, id) => {
      if (!activeLiveIds.has(id)) liveBubbleHeightsRef.current.delete(id);
    });
  }, [captions]);

  const handleClearLog = useCallback(() => {
    setClearLogConfirmOpen(true);
  }, []);

  const confirmClearLog = useCallback(() => {
    setClearLogConfirmOpen(false);
    onClearAll?.();
    setFooterStatus('Transcript cleared');
    setTimeout(() => setFooterStatus(''), 2500);
  }, [onClearAll]);

  const cancelClearLog = useCallback(() => setClearLogConfirmOpen(false), []);

  const handleCopyPinned = useCallback(() => {
    const pinnedText = pinnedCaptions
      .map((c) => `[${(c.lang || 'en').toUpperCase()}] ${c.text}`)
      .join('\n---\n');
    navigator.clipboard.writeText(pinnedText).then(
      () => {
        setFooterStatus(`Copied ${pinnedCaptions.length} pinned message(s)`);
        setTimeout(() => setFooterStatus(''), 2500);
      },
      () => setFooterStatus('Copy failed — check browser permissions'),
    );
  }, [pinnedCaptions]);
  
  // Smart Bubble Compression (v4.133.0): expansion is remembered by TEXT
  // SIGNATURE, not caption id — ids mutate on seal/split/dedupe, which used to
  // re-collapse a bubble you had opened (or were reading) mid-call.
  const [expandedIds, setExpandedIds] = useState(new Set());
  const toggleExpand = (capOrId) => {
    const key = typeof capOrId === 'string' ? capOrId : bubbleExpandKey(capOrId);
    const id = typeof capOrId === 'string' ? capOrId : capOrId?.id;
    setExpandedIds(prev => {
      const next = new Set(prev);
      const has = next.has(key) || (id && next.has(id));
      if (has) {
        next.delete(key);
        if (id) next.delete(id);
      } else {
        next.add(key);
        if (id) next.add(id);
      }
      // Soft cap — never let this set grow unbounded over a long shift.
      if (next.size > 400) return new Set([key, id].filter(Boolean));
      return next;
    });
  };
  const isBubbleExpanded = (cap) =>
    expandedIds.has(bubbleExpandKey(cap)) || expandedIds.has(cap.id);

  useEffect(() => {
    safeSet('catint_pinned_msgs', JSON.stringify(pinnedCaptions));
  }, [pinnedCaptions]);

  // Caption ids change on seal/split — keep pins aligned to the same bubble.
  useEffect(() => {
    setPinnedCaptions((prev) => {
      const next = migratePinnedCaptions(prev, captions);
      return next === prev ? prev : next;
    });
  }, [captions]);

  useEffect(() => {
    const onPairChange = (e) => setLanguagePair(e.detail || loadLanguagePair());
    window.addEventListener(LANG_PAIR_CHANGED_EVENT, onPairChange);
    return () => window.removeEventListener(LANG_PAIR_CHANGED_EVENT, onPairChange);
  }, []);

  // UX: new call start should clear pinned messages (without affecting transcript history).
  useEffect(() => {
    const onPinnedCleared = () => {
      setPinnedCaptions([]);
      safeSet('catint_pinned_msgs', JSON.stringify([]));
    };
    window.addEventListener('catint_pinned_cleared', onPinnedCleared);
    return () => window.removeEventListener('catint_pinned_cleared', onPinnedCleared);
  }, []);

  const captionRenderKeys = useMemo(
    () => buildCaptionContinuityKeys(captions),
    [captions],
  );
  const lastAudioChunkAt = connectProgress?.lastAudioChunkAt || 0;
  const showSttSoundbar = connectionState === 'connected';
  // v4.195.1: wave = someone audibly speaking (local RMS), not "a packet
  // left" — the recorder ships silence too, so the old wave never stopped.
  const lastLoudAudioAt = connectProgress?.lastLoudAudioAt || 0;
  const audioHot = lastLoudAudioAt > 0 && sttNow - lastLoudAudioAt < 1400;
  // v4.195.1: Text/UI dots go back to "waiting" after 30s with no words
  // (they stayed lit forever after the first word of the session).
  const freshAt = (t) => (t && sttNow - t < DG_FRESH_TEXT_MS ? t : 0);
  const enConfidence = Number.isFinite(connectProgress?.lastSocketEnConfidence)
    ? connectProgress.lastSocketEnConfidence
    : null;
  const esConfidence = Number.isFinite(connectProgress?.lastSocketEsConfidence)
    ? connectProgress.lastSocketEsConfidence
    : null;
  const enRecent = (connectProgress?.lastSocketEnMessageAt || 0) > 0 && sttNow - connectProgress.lastSocketEnMessageAt < 4500;
  const esRecent = (connectProgress?.lastSocketEsMessageAt || 0) > 0 && sttNow - connectProgress.lastSocketEsMessageAt < 4500;
  const sttWinner =
    enConfidence !== null && esConfidence !== null
      ? enConfidence >= esConfidence ? 'en' : 'es'
      : enConfidence !== null ? 'en' : esConfidence !== null ? 'es' : null;
  const emptyReplyStreak = connectProgress?.emptyTranscriptStreak || 0;
  // v4.169.0: the line that would have saved the call. One socket transcribing
  // a whole conversation while the other returns nothing is a SILENT failure —
  // every bubble still gets stamped with the working socket's language, so the
  // operator reads Spanish labelled English. Say it in words, in the rail.
  const socketHealth = buildSocketHealth({
    now: sttNow,
    en: {
      words: connectProgress?.socketEnWords || 0,
      lastTextAt: connectProgress?.socketEnLastTextAt || 0,
    },
    es: {
      words: connectProgress?.socketEsWords || 0,
      lastTextAt: connectProgress?.socketEsLastTextAt || 0,
    },
  });
  const repeatedEmptyReply =
    emptyReplyStreak >= 3 &&
    (connectProgress?.lastEmptyTranscriptAt || 0) > (connectProgress?.lastTranscriptStringAt || 0);
  const captureBlocked =
    (connectProgress?.captureBlockedAt || 0) > (connectProgress?.lastCaptionCommitAt || 0);
  const sttPipelineSteps = [
    { key: 'heard', label: 'In', title: 'Speech-level sound heard by app', at: lastLoudAudioAt },
    { key: 'sent', label: 'API', title: 'Sound sent to Deepgram', at: lastAudioChunkAt },
    { key: 'dgmsg', label: 'DG', title: 'Deepgram answered', at: connectProgress?.lastDeepgramMessageAt || 0 },
    {
      key: 'text',
      label: repeatedEmptyReply ? 'Text×' : 'Text',
      title: repeatedEmptyReply
        ? `Deepgram answered empty ${emptyReplyStreak} times; check the selected audio source`
        : 'Deepgram returned text',
      at: freshAt(connectProgress?.lastTranscriptStringAt),
      state: repeatedEmptyReply ? 'empty' : undefined,
    },
    {
      key: 'commit',
      label: captureBlocked ? 'UI×' : 'UI',
      title: captureBlocked ? 'Text returned but the active-call capture gate blocked rendering' : 'Text committed to UI',
      at: freshAt(connectProgress?.lastCaptionCommitAt),
      state: captureBlocked ? 'blocked' : undefined,
    },
  ];

  /** One word count per silence-to-silence turn — show only on the last bubble of that turn. */
  const turnDisplayMeta = useMemo(() => {
    const lastIndexByTurn = {};
    const maxCountByTurn = {};
    captions.forEach((cap, i) => {
      const tid = cap.turnId || `solo-${cap.id}`;
      lastIndexByTurn[tid] = i;
      const tc = cap.turnWordCount ?? 0;
      if (tc > (maxCountByTurn[tid] ?? 0)) maxCountByTurn[tid] = tc;
    });
    return { lastIndexByTurn, maxCountByTurn };
  }, [captions]);

  const togglePin = (cap) => {
    setPinnedCaptions((prev) => togglePinEntry(prev, cap));
  };

  const bumpManualRetranslate = (cap) => {
    const capId = cap?.id;
    if (!capId) return;
    setTranslationBumps((prev) => ({
      ...prev,
      [capId]: (prev[capId] || 0) + 1,
    }));
  };

  /**
   * v4.145.0 — snap the pane to the newest line.
   * Instant, and scoped to the pane: `scrollIntoView` could move the whole
   * page and could silently do nothing, which is how the last line was lost.
   */
  const followLatest = useCallback(() => {
    const pane = scrollAreaRef.current;
    if (!pane) return;
    programmaticUntilRef.current = Date.now() + PROGRAMMATIC_GRACE_MS;
    pane.scrollTop = pane.scrollHeight;
    if (!isAtBottom(readScrollMetrics(pane))) {
      // Engine refused the direct scroll (rare) — last resort.
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
    setNewBelow(0);
  }, []);

  /** Called by wheel / pointer / touch so a real gesture is never mistaken for layout. */
  const markUserGesture = useCallback(() => {
    userGestureRef.current = true;
    if (gestureTimerRef.current) clearTimeout(gestureTimerRef.current);
    gestureTimerRef.current = setTimeout(() => {
      userGestureRef.current = false;
    }, USER_GESTURE_HOLD_MS);
  }, []);

  /** Operator read back — pause + re-arm later so a long read is not punished. */
  const pauseFollow = useCallback(() => {
    userScrolledUpRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      userScrolledUpRef.current = false;
      if (stickyBottomRef.current) followLatest();
    }, RESUME_AFTER_USER_MS);
  }, [followLatest]);

  // v4.183.0: reading = mouse over the transcript (moved within READING_HOLD_MS).
  // While reading, new lines never yank the view; the "⬇ N new" button counts them.
  const readingRef = useRef(false);
  const readingTimerRef = useRef(null);
  const following = () =>
    shouldFollow({
      sticky: stickyBottomRef.current,
      userScrolledUp: userScrolledUpRef.current,
      reading: readingRef.current,
    });
  const stopReading = () => {
    if (readingTimerRef.current) clearTimeout(readingTimerRef.current);
    readingTimerRef.current = null;
    if (!readingRef.current) return;
    readingRef.current = false;
    if (following()) followLatest();
  };
  const handleReadingMove = () => {
    readingRef.current = true;
    if (readingTimerRef.current) clearTimeout(readingTimerRef.current);
    readingTimerRef.current = setTimeout(stopReading, READING_HOLD_MS);
  };

  /**
   * New captions → follow. Two settle passes because a bubble can grow AFTER
   * this render (translation line arrives, morph, re-wrap) — that late growth
   * was the other way the newest line slid out of view.
   */
  useEffect(() => {
    const count = captions.length;
    const prevCount = lastCountRef.current;
    lastCountRef.current = count;

    if (following()) {
      followLatest();
      const settle = () => { if (following()) followLatest(); };
      const t1 = setTimeout(settle, 120);
      const t2 = setTimeout(settle, 400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (count > prevCount) setNewBelow((n) => n + (count - prevCount));
    return undefined;
  }, [captions, followLatest]);

  const toggleStickyBottom = () => {
    // Paused mid-read? The button reads "⬇ N new" — a click means
    // "take me to the newest line", not "switch the follow off".
    if (stickyBottomRef.current && (userScrolledUpRef.current || readingRef.current || newBelow > 0)) {
      userScrolledUpRef.current = false;
      readingRef.current = false; // v4.183.0: "⬇ N new" while reading = jump, not "follow off"
      if (readingTimerRef.current) clearTimeout(readingTimerRef.current);
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      followLatest();
      return;
    }
    const next = !stickyBottomRef.current;
    setStickyBottom(next);
    try {
      localStorage.setItem(STICKY_BOTTOM_KEY, next ? '1' : '0');
    } catch (_) {}
    if (next) {
      userScrolledUpRef.current = false;
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      followLatest();
    }
  };

  const handleScroll = () => {
    const pane = scrollAreaRef.current;
    if (!pane) return;
    const cause = classifyScroll({
      now: Date.now(),
      programmaticUntil: programmaticUntilRef.current,
      userGesture: userGestureRef.current,
    });
    if (cause === 'self') return; // we did this — never a reason to stop

    if (isAtBottom(readScrollMetrics(pane))) {
      userScrolledUpRef.current = false;
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      if (newBelow) setNewBelow(0);
      return;
    }
    if (cause === 'user') {
      pauseFollow();
      return;
    }
    // 'content': scroll-anchoring / late layout moved us — put the newest line back.
    if (following()) followLatest();
  };

  const handleWheel = (e) => {
    markUserGesture();
    const pane = scrollAreaRef.current;
    // Only a real scroll-up intent pauses; a wheel on a short transcript does not.
    if (e.deltaY < 0 && pane && pane.scrollTop > 0) pauseFollow();
  };

  /** Wheel/drag/scrollbar all mean "a human is moving this pane". */
  const handlePointerDown = () => markUserGesture();
  const handlePointerUp = () => markUserGesture();
  const handleTouchStart = () => markUserGesture();
  const handleTouchMove = () => markUserGesture();

  useEffect(() => () => {
    if (gestureTimerRef.current) clearTimeout(gestureTimerRef.current);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    if (readingTimerRef.current) clearTimeout(readingTimerRef.current);
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      if (popoverTimerRef.current) clearTimeout(popoverTimerRef.current);
      const selection = window.getSelection();
      const text = selection.toString().trim();
      if (text && text.length < 50) {
        popoverTimerRef.current = setTimeout(() => {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          setPopover({ show: true, x: rect.left + window.scrollX, y: rect.top + window.scrollY - 40, text });
        }, 800);
      } else {
        if (popover.show) setPopover(p => ({ ...p, show: false }));
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (popoverTimerRef.current) clearTimeout(popoverTimerRef.current);
    };
  }, [popover.show]);

  const handleLookup = (text) => {
    const url = `https://www.linguee.com/english-spanish/search?source=auto&query=${encodeURIComponent(text)}`;
    window.open(url, 'LingueeLookup', 'width=800,height=600,scrollbars=yes');
    setPopover(p => ({ ...p, show: false }));
  };

  return (
    <div className="transcription-area" style={{ 
      position: 'relative', background: 'var(--panel-bg)', border: '1px solid #18181b', borderRadius: 0,
      display: 'flex', flexDirection: 'column', height: '100%'
    }}>
      {popover.show && (
        <div style={{
          position: 'fixed', top: popover.y, left: popover.x, zIndex: 10000,
          background: 'var(--accent-primary)', color: '#000', padding: '4px 10px',
          borderRadius: 0, fontSize: '0.8rem', fontWeight: 900, cursor: 'pointer'
        }} onClick={() => handleLookup(popover.text)}>
          LOOKUP: "{popover.text}"
        </div>
      )}

      {isZombieCall && connectionState !== 'connected' && (
        <div 
          onClick={onReconnect}
          className="zombie-reattach-banner"
          style={{
            position: 'absolute', top: '10px', left: '10px', right: '10px', zIndex: 1001,
            background: '#f59e0b', color: '#000', padding: '0.8rem',
            borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '0.2rem', cursor: 'pointer', fontWeight: 900, fontSize: '0.9rem',
            border: '2px solid #000', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            animation: 'pulseGlow 2s infinite'
          }}
        >
          <div style={{ fontSize: '1.2rem' }}>🟡 AUDIO DISCONNECTED — CALL STILL ACTIVE</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.9, textAlign: 'center', maxWidth: '36rem' }}>
            Click here or press the yellow 🟡 button above. Transcript and call timer are preserved — no need to Stop.
          </div>
          <div style={{ fontSize: '0.65rem', opacity: 0.85, textAlign: 'center' }}>
            {ioRouteHint} · STT: {connectionState === 'connected' ? `disconnected (${reattachLabelForMode(resolveIdleAudioMode({ micTestMode, audioSourceMode }))})` : connectionState}
          </div>
          <div style={{ fontSize: '0.7rem', marginTop: '4px', textDecoration: 'underline' }}>[RE-ATTACH AUDIO]</div>
        </div>
      )}

      {/* Post-Call Summary Toast */}
      {(!isActive && lastCallSummary) && (
        <div style={{
          position: 'absolute', top: '8px', left: '8px', right: '8px', zIndex: 1000,
          background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(59, 130, 246, 0.4)',
          borderRadius: '6px', padding: '0.5rem 0.7rem',
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 900, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              📋 LAST CALL SUMMARY · {lastCallSummary.timestamp}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#fff', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
              {lastCallSummary.numbers.length > 0 && (
                <span>🔢 {lastCallSummary.numbers.join(', ')}</span>
              )}
              {lastCallSummary.dollars.length > 0 && (
                <span style={{ color: '#fcd34d' }}>💰 {lastCallSummary.dollars.join(', ')}</span>
              )}
              {/* v4.99.0: minutes banked from wall-clock (STT saw no speech). */}
              {lastCallSummary.noStt && (
                <span style={{ color: '#fdba74' }} title="Deepgram reported no speech — minutes banked from wall clock. Correct via the heatmap day editor if needed.">🎧 banked without STT</span>
              )}
              {lastCallSummary.numbers.length === 0 && lastCallSummary.dollars.length === 0 && (
                <span style={{ opacity: 0.5 }}>No key data extracted</span>
              )}
            </div>
          </div>
          <button onClick={() => setLastCallSummary(null)} type="button" aria-label="Dismiss call summary" style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem', padding: '0 4px' }} title="Dismiss">✕</button>
        </div>
      )}

      {/* v4.130.1 Last Call seal — read-only transcript of the finished call.
          Survives STOP/refresh/update; expires on next call, End Day, or 🗑. */}
      {(!isActive && lastCallArchive && captions.length === 0) && (
        <div id="last-call-seal" style={{
          margin: '0 8px 0.5rem', border: '1px solid rgba(52, 211, 153, 0.35)',
          borderRadius: '6px', background: 'rgba(6, 20, 16, 0.85)', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.5rem' }}>
            <button
              type="button"
              onClick={() => setLastCallOpen((o) => !o)}
              aria-expanded={lastCallOpen}
              style={{ background: 'transparent', border: 'none', color: '#6ee7b7', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.04em', padding: 0 }}
              title={lastCallOpen ? 'Collapse last call' : 'Expand last call'}
            >
              {lastCallOpen ? '▾' : '▸'} 📞 LAST CALL · {new Date(lastCallArchive.endedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {lastCallArchive.captions.length} msgs
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => { if (window.confirm('Delete the sealed last-call transcript?')) clearLastCallArchive?.(); }}
              aria-label="Delete last-call transcript"
              style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '0.8rem', padding: '0 4px' }}
              title="Delete the sealed last-call transcript now"
            >
              🗑
            </button>
          </div>
          {lastCallOpen && (
            <div style={{ maxHeight: '30vh', overflowY: 'auto', padding: '0 0.5rem 0.4rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {lastCallArchive.captions.map((cap) => {
                const sealed = cap?.translations ? composeCaptionTranslation(cap.translations) : '';
                return (
                  <div key={`last-${cap.id}`} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', padding: '0.2rem 0.4rem', fontSize: '0.75rem', lineHeight: 1.3 }}>
                    <div style={{ color: '#fff' }}>{cap.text}</div>
                    {sealed && sealed !== cap.text && (
                      <div style={{ color: 'rgba(255,255,255,0.6)' }}>{sealed}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div 
        className="scroll-area"
        id="transcript-pane"
        style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }} 
        onScroll={handleScroll}
        onWheel={handleWheel}
        onMouseEnter={handleReadingMove}
        onMouseMove={handleReadingMove}
        onMouseLeave={stopReading}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        ref={scrollAreaRef}
      >
        {pinnedCaptions.length > 0 && (
          <div className="pinned-transcript-section" style={{
            flexShrink: 0,
            marginBottom: '0.5rem',
            paddingBottom: '0.4rem',
            borderBottom: '1px solid rgba(34, 197, 94, 0.35)',
          }}>
            <div style={{ fontSize: '0.6rem', fontWeight: 900, color: 'var(--accent-primary)', marginBottom: '0.35rem', letterSpacing: '0.06em' }}>
              📌 PINNED ({pinnedCaptions.length})
            </div>
            {pinnedCaptions.map((cap) => (
              <div key={`pin-${cap.id}`} className="transcript-bubble pinned-transcript-bubble" style={{
                marginTop: '0.35rem',
                border: '1px solid var(--accent-primary)',
                background: 'rgba(34, 197, 94, 0.1)',
                ...getBubbleStyle(cap.text, true, cap.lang, languagePair),
              }}>
                <MemoTranslatedBubble
                  id={cap.id}
                  text={cap.text}
                  lang={cap.lang}
                  playTTS={playTTS}
                  stopTTS={stopTTS}
                  playingUrl={playingUrl}
                  prefetchTTS={prefetchTTS}
                  reverse={shouldReverseBubble(cap.lang, languagePair)}
                  ttsMode={ttsMode}
                  wordCount={cap.text.trim().split(/\s+/).length}
                  shouldPrefetch={false}
                  isPinned={true}
                  onTogglePin={() => togglePin(cap)}
                  forceTranslateKey={translationBumps[cap.id] ?? 0}
                  onManualRetranslate={() => bumpManualRetranslate(cap)}
                  languagePair={languagePair}
                  protectionsActive={protectionsActive}
                  mockTranslation={cap._devMockTranslation}
                  userCorrected={Boolean(cap.userCorrected)}
                  userTranslationOverride={cap.userTranslationOverride || null}
                  persistedTranslations={cap.translations || null}
                  onPersistTranslation={persistCaptionTranslation}
                  canEdit={cap.isFinal !== false}
                  correctionsRev={correctionsRev}
                  domainRepairOn={domainRepairOn}
                  negationGuardOn={negationGuardOn}
                  wordConfidence={cap.wordConfidence || null}
                  {...makeEditHandlers(cap)}
                />
                <BubbleTs cap={cap} />
                <button
                  type="button"
                  className="bubble-pin-btn is-pinned"
                  onClick={() => togglePin(cap)}
                  title="Unpin message"
                  aria-label="Unpin message"
                >
                  📌
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: '1 1 auto' }} />

        {captions.length === 0 && pinnedCaptions.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isZombieCall ? 0.85 : 1 }}>
            {isZombieCall && connectionState !== 'connected' ? (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', textAlign: 'center', padding: '0 1rem', color: '#fbbf24' }}>
                Re-attach audio — your timer and transcript are saved. Press the green Re-attach button.
              </div>
            ) : showOffCallGuide && !newcomerSessionHidden && !isNewcomerGuideDismissed() ? (
              <NewcomerIdleGuide
                audioAttached={audioAttached}
                micTestMode={micTestMode}
                connectionState={connectionState}
                isActive={isActive}
                onHideSession={() => setNewcomerSessionHidden(true)}
              />
            ) : null}
          </div>
        )}

        {captions.map((cap, i) => {
          if (!cap.text || !cap.text.trim()) {
            if (cap.id && !blankFlaggedRef.current.has(cap.id)) {
              blankFlaggedRef.current.add(cap.id);
              flagVanish('ui_blank_caption_skipped', {
                id: cap.id,
                turnId: cap.turnId,
                before: '(row present)',
                after: '',
                derender: true,
                force: true,
                stage: 'TranscriptionBoard.map',
                extra: { note: 'row exists but text empty — not rendered' },
              });
            }
            return null;
          }
          const isSameAsPrevious = i > 0 && captions[i-1].lang === cap.lang;
          // v4.152.0: cross-message repeat dimming — later copies of wording the
          // previous bubble already showed are grayed (display-only).
          const prevCap = i > 0 ? captions[i - 1] : null;
          const prevText = isSameAsPrevious ? (prevCap?.text || '') : '';
          const prevTranslationText = isSameAsPrevious
            ? (Object.values(prevCap?.translations || {}).pop()?.text || '')
            : '';
          const wordCount = countBubbleWords(cap.text);
          const tid = cap.turnId || `solo-${cap.id}`;
          const turnWordCount = turnDisplayMeta.maxCountByTurn[tid] ?? cap.turnWordCount ?? 0;
          const showTurnWordCount = i === turnDisplayMeta.lastIndexByTurn[tid];
          if (isCaptionPinned(pinnedCaptions, cap)) return null;
          // UI_SPLIT_HEURISTIC:
          // This is the current UI-only signal we use to trigger the "flow/move" look.
          // IMPORTANT: The real algorithm-selected moved text section happens inside
          // `useDeepgram.js` (see `ALGO_MOVED_SECTION` comment), and this heuristic may
          // differ. Blue should be attached to ONLY the moved section substring.
          const isSplitContinuation = isSameAsPrevious && wordCount < 50;
          const isLive = cap.isFinal === false;
          // v4.133.0: never clip (a) the live row, (b) the trailing rows you are
          // reading right now, (c) any row with medical data. The full
          // containsCriticalData pass also runs: protection must not depend on
          // the fast regex alone (e.g. spelled-out doses without units nearby).
          const isRecentRow = i >= captions.length - ALWAYS_VISIBLE_TAIL_ROWS;
          const isLongBubble = shouldAutoCollapseBubble({
            wordCount,
            isLive,
            isRecent: isRecentRow,
            isCritical: isCriticalBubbleText(cap.text) || containsCriticalData(cap.text),
          });
          const isExpanded = isBubbleExpanded(cap);
          const bubbleStyle = getBubbleStyle(cap.text, isLive, cap.lang, languagePair);
          const stableMinHeight = isLive
            ? liveBubbleHeightsRef.current.get(cap.id)?.height
            : null;
          const textLen = (cap.text || '').length;

          return (
            <div
              ref={(node) =>
                isLive ? rememberLiveBubbleHeight(cap.id, node, textLen) : undefined
              }
              key={captionRenderKeys[i]}
              className={`transcript-bubble${isLive ? ' is-live' : ' is-sealed'}${isSplitContinuation ? ' is-flowed' : ''}${cap.userCorrected || cap.userTranslationOverride ? ' is-user-corrected' : ''}`}
              style={{
              marginTop: isSplitContinuation ? '0rem' : '0.25rem',
              border: '1px solid transparent',
              background: bubbleStyle.backgroundColor,
              minHeight: isLive && stableMinHeight ? `${stableMinHeight}px` : undefined,
              ...bubbleStyle
            }}>
              
              <div style={{ maxHeight: isLongBubble && !isExpanded ? '5.5rem' : 'none', overflow: isLongBubble && !isExpanded ? 'hidden' : 'visible', transition: 'max-height 0.3s ease' }}>
                <MemoTranslatedBubble 
                  id={cap.id} text={cap.text} lang={cap.lang} playTTS={playTTS} stopTTS={stopTTS} playingUrl={playingUrl} prefetchTTS={prefetchTTS} 
                  reverse={shouldReverseBubble(cap.lang, languagePair)} ttsMode={ttsMode} turnWordCount={turnWordCount} showTurnWordCount={showTurnWordCount} shouldPrefetch={i >= captions.length - 3} 
                  isPinned={false} onTogglePin={() => togglePin(cap)}
                  forceTranslateKey={translationBumps[cap.id] ?? 0}
                  onManualRetranslate={() => bumpManualRetranslate(cap)}
                  tailPreviewText={null}
                  isFinal={cap.isFinal !== false}
                  allowAutoRetranslate={i >= captions.length - 2}
                  languagePair={languagePair}
                  protectionsActive={protectionsActive}
                  mockTranslation={cap._devMockTranslation}
                  userCorrected={Boolean(cap.userCorrected)}
                  userTranslationOverride={cap.userTranslationOverride || null}
                  continuityKey={captionRenderKeys[i]}
                  persistedTranslations={cap.translations || null}
                  onPersistTranslation={persistCaptionTranslation}
                  canEdit={!isLive}
                  correctionsRev={correctionsRev}
                  domainRepairOn={domainRepairOn}
                  negationGuardOn={negationGuardOn}
                  wordConfidence={cap.wordConfidence || null}
                  prevText={prevText}
                  prevTranslationText={prevTranslationText}
                  {...makeEditHandlers(cap)}
                />
              </div>
              
              {isLongBubble && (
                <button className="bubble-expand-btn" onClick={() => toggleExpand(cap)}>
                  {isExpanded ? '▲ collapse' : `··· ${wordCount} words ···`}
                </button>
              )}
              
              <BubbleTs cap={cap} />
              <button
                type="button"
                className="bubble-pin-btn"
                onClick={() => togglePin(cap)}
                title="Pin message (keeps it visible at top for voicemail / callouts)"
                aria-label="Pin message"
              >
                📍
              </button>
            </div>
          );
        })}
        <div id="scroll-bottom-anchor" ref={bottomRef} style={{ height: '22px', flexShrink: 0, pointerEvents: 'none' }} />
      </div>

      <button
        type="button"
        id="sticky-bottom-toggle"
        className={`sticky-bottom-toggle${stickyBottom ? ' is-on' : ''}${newBelow > 0 ? ' has-new' : ''}`}
        onClick={toggleStickyBottom}
        aria-pressed={stickyBottom}
        title={
          newBelow > 0
            ? `${newBelow} new line(s) below — click to jump to the newest line`
            : stickyBottom
              ? 'Sticky bottom ON — always showing the newest transcript line (click to pause)'
              : 'Sticky bottom OFF — click to follow the newest line'
        }
      >
        {newBelow > 0 ? `⬇ ${newBelow} new` : stickyBottom ? '⬇ sticky' : '⬇ off'}
      </button>

      {showSttSoundbar && (
        <div className={`stt-process-rail stt-process-rail--bottom${audioHot ? ' is-audio-hot' : ''}`} aria-live="polite">
          {/* v4.169.0: the failure that cost a real call. One socket goes silent,
              the other transcribes everything, and every bubble gets stamped
              with the working socket's language — so the operator reads Spanish
              labelled English. First child of the rail, in words, no scrolling
              and no extra row: this is not a tooltip. */}
          {socketHealth.alert && (
            <span className="stt-socket-alert" role="status">
              ⚠ {socketHealth.alert.text}
            </span>
          )}
          {!socketHealth.alert && socketHealth.quiet && (
            <span className="stt-socket-quiet">· {socketHealth.quiet.text}</span>
          )}
          <div className="stt-sound-wave" title={audioHot ? 'Someone is speaking — audio is going to Deepgram' : 'Quiet — no speech-level sound'}>
            {Array.from({ length: 14 }).map((_, i) => <span key={i} style={{ '--bar-i': i }} />)}
          </div>
          {sttPipelineSteps.map((step) => (
            <span
              key={step.key}
              className={`stt-process-step is-${step.state || (step.at ? 'done' : 'waiting')}`}
              title={step.at ? `${step.title || step.label}: ${sttNow - step.at}ms ago` : `${step.title || step.label}: waiting`}
            >
              <span className="stt-process-dot" />
              {step.label}
            </span>
          ))}
          {/* v4.169.0: per-socket HEALTH, not two bare confidence numbers.
              On a real call one socket transcribed everything while the other
              returned nothing, and "EN 65% / ES 0%" reads like two numbers
              rather than "one side is dead". These say it in words. */}
          <span
            className={`stt-lane stt-lane--en${enRecent ? ' is-recent' : ''}${sttWinner === 'en' ? ' is-winner' : ''}${socketHealth.alert?.side === 'en' ? ' is-starved' : ''}`}
            title={socketHealth.en.title}
          >
            EN {socketHealth.en.label}
          </span>
          <span
            className={`stt-lane stt-lane--es${esRecent ? ' is-recent' : ''}${sttWinner === 'es' ? ' is-winner' : ''}${socketHealth.alert?.side === 'es' ? ' is-starved' : ''}`}
            title={socketHealth.es.title}
          >
            ES {socketHealth.es.label}
          </span>
          <span className="stt-process-confidence">
            w{connectProgress?.lastWordConfidenceCount || 0}
          </span>
        </div>
      )}

      <BubbleCorrectionEditor
        open={Boolean(correctionEditor)}
        field={correctionEditor?.field || 'source'}
        draft={correctionEditor?.draft || ''}
        sourceLang={correctionEditor?.sourceLang || 'en'}
        targetLang={correctionEditor?.targetLang}
        onDraftChange={(v) => setCorrectionEditor((prev) => (prev ? { ...prev, draft: v } : prev))}
        onSave={handleCorrectionSave}
        onCancel={closeCorrectionEditor}
      />

      {/* Simplified Footer Toolbar */}
      <div className="transcription-footer" data-guide="pin" style={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px',
        borderTop: '1px solid #18181b', background: 'var(--panel-bg)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.65rem'
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span
            className="transcript-audio-safe"
            title="Transcript audio is manual only. Messages never auto-play."
          >
            Audio: manual
          </span>
          {pinnedCaptions.length > 0 && (
            <button
              type="button"
              onClick={handleCopyPinned}
              style={{ background: 'transparent', color: '#34d399', border: 'none', cursor: 'pointer' }}
              title={`Copy ${pinnedCaptions.length} pinned message(s) to clipboard`}
              aria-label={`Copy ${pinnedCaptions.length} pinned messages`}
            >
              📋 COPY_PINNED({pinnedCaptions.length})
            </button>
          )}
          {footerStatus && (
            <span className="transcription-footer-status" role="status" aria-live="polite">
              {footerStatus}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={handleClearLog}
            aria-label="Clear transcript log"
            title="Clear transcript (pinned messages kept)"
            style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            Clear log
          </button>
          <button
            type="button"
            onClick={stopTTS}
            disabled={!isPlaying}
            aria-label="Stop AI voice playback"
            title="Stop text-to-speech"
            style={{ background: 'transparent', color: isPlaying ? 'var(--danger)' : '#333', border: 'none', cursor: isPlaying ? 'pointer' : 'default' }}
          >
            Stop voice
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={clearLogConfirmOpen}
        title="Clear transcript log?"
        message="Unpinned messages will be removed. Pinned messages stay. This cannot be undone."
        confirmLabel="Clear log"
        cancelLabel="Keep log"
        danger
        onConfirm={confirmClearLog}
        onCancel={cancelClearLog}
      />
    </div>
  );
};
